/**
 * Idle Modal — shows on every authenticated tab when 20 min of inactivity
 * is reached in any tab in this browser.
 *
 * Two copy variants based on the `source` prop:
 *   - 'self'   — THIS tab is the one that went idle. Personal copy:
 *                "Your session is inactive, would you like to proceed?"
 *   - 'remote' — Another (sibling) tab went idle; this tab is still
 *                active. Ambient copy:
 *                "One or more inactive tabs are open. Please close the
 *                inactive tabs or choose Continue."
 *
 * In both cases the user picks Continue or Logout; the choice broadcasts
 * to every tab so they all stay in sync. The countdown is shown on both
 * variants so every tab gets the same 10s reaction window.
 *
 * Props:
 *   open        — boolean, whether the modal is visible
 *   source      — 'self' | 'remote', which tab originated the warning
 *   countdown   — number, seconds remaining (drives the visible counter)
 *   onProceed   — () => void, called when user clicks "Yes, Proceed"
 *   onLogout    — () => void, called when user clicks "No, Logout" or
 *                 when the countdown reaches 0
 */
const IdleModal = ({
  open,
  source = "self",
  countdown,
  onProceed,
  onLogout,
}) => {
  // Don't render the DOM tree at all when closed — avoids a11y noise.
  if (!open) return null;

  // Asymmetric copy. Same buttons, same countdown, different framing.
  const isRemote = source === "remote";
  const title = isRemote
    ? "Inactive tabs detected"
    : "Your session is inactive";
  const body = isRemote
    ? "You have inactive tabs open. Would you like to continue in this tab or close inactive tabs."
    : "Would you like to continue working in this tab.";

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="idle-modal-title"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" aria-hidden="true" />

      {/* Modal panel */}
      <div className="relative z-10 w-full max-w-md mx-4 bg-white dark:bg-gray-800 rounded-lg shadow-2xl overflow-hidden">
        <div className="px-6 pt-6 pb-2 text-center">
          <div className="mx-auto mb-3 h-12 w-12 rounded-full bg-primaryColor/10 flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-primaryColor"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2
            id="idle-modal-title"
            className="text-lg font-semibold text-gray-900 dark:text-gray-100"
          >
            {title}
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            {body}
          </p>
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
            You will be logged out in{" "}
            <span className="font-semibold text-primaryColor">
              {countdown}s
            </span>
          </p>
        </div>

        <div className="px-6 pb-6 pt-4 flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={onProceed}
            className="px-4 py-2 rounded-md bg-primaryColor text-white text-sm font-medium hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primaryColor focus:ring-offset-2 transition"
          >
            Yes, Continue
          </button>
          <button
            type="button"
            onClick={onLogout}
            className="px-4 py-2 rounded-md bg-white text-gray-700 border border-gray-300 text-sm font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2 transition dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600 dark:hover:bg-gray-600"
          >
            No, Logout
          </button>
        </div>
      </div>
    </div>
  );
};

export default IdleModal;
